import { useCallback, useRef, type KeyboardEvent } from "react";

/**
 * The house tablist keyboard law (A3-CLOSE): Arrow keys move roving focus
 * across the composite's `role="tab"` members with wrap-around, Home/End jump
 * to the edges, and activation stays manual (Enter/Space are the button's
 * own). The tablist element must hold ONLY tab members so roving focus never
 * lands on a non-tab.
 */
export function useRovingTablist<T extends HTMLElement = HTMLDivElement>(
  // FD-CAN-17 (2026-09-01): the context dock stands its tablist vertical —
  // a vertical list answers Up/Down where a horizontal one answers
  // Left/Right, per the tablist pattern.
  options: { readonly orientation?: "horizontal" | "vertical" } = {},
) {
  const { orientation = "horizontal" } = options;
  const listRef = useRef<T | null>(null);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<T>) => {
      const buttons = [
        ...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []),
      ];
      if (buttons.length === 0) return;
      const current = buttons.indexOf(event.target as HTMLButtonElement);
      if (current < 0) return;
      const forward = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
      const backward = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
      let next: number | null = null;
      if (event.key === forward) next = (current + 1) % buttons.length;
      else if (event.key === backward) next = (current - 1 + buttons.length) % buttons.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = buttons.length - 1;
      if (next === null) return;
      event.preventDefault();
      buttons[next]?.focus();
    },
    [orientation],
  );

  return { listRef, onKeyDown };
}
